type WaveformPeaksInput =
  | number[]
  | readonly number[]
  | Array<number[] | readonly number[]>
  | ReadonlyArray<ReadonlyArray<number>>;

/**
 * Bucket aggregation modes for waveform downsample comparison.
 * Flip `ACTIVE_WAVEFORM_BUCKET_AGG` to switch which aggregator runs.
 */
export type WaveformBucketAggMode =
  | 'rms'
  | 'p75'
  | 'p90'
  | 'max'
  | 'weighted';

export const ACTIVE_WAVEFORM_BUCKET_AGG: WaveformBucketAggMode = 'p75';

/** Weight for RMS in weighted mode: result = α·RMS + (1−α)·max. */
const WEIGHTED_RMS_RATIO = 0.6;

type BucketAggregator = (amps: number[]) => number;

/**
 * Number of bars WaveSurfer will draw ≈ floor(containerWidth / (barWidth + barGap)).
 * e.g. 615px width, barWidth=2, barGap=0 → 307 pairs.
 */
export const getTargetWaveformBarPairs = (
  containerWidthPx: number,
  barWidth: number = 2,
  barGap: number = 0,
): number => {
  const step = barWidth + barGap;
  if (containerWidthPx <= 0 || step <= 0) return 0;
  return Math.max(1, Math.floor(containerWidthPx / step));
};

const toFlatPeaks = (peaks: WaveformPeaksInput): number[] | null => {
  if (!peaks?.length) return null;

  if (typeof peaks[0] === 'number') {
    return Array.from(peaks as ArrayLike<number>);
  }

  // Multi-channel: use first channel (WaveSurfer default merge path also prefers ch0)
  const firstChannel = (peaks as Array<ArrayLike<number>>)[0];
  return firstChannel?.length ? Array.from(firstChannel) : null;
};

const peakAmplitude = (min: number, max: number): number =>
  Math.max(Math.abs(min), Math.abs(max));

const percentileLinear = (sortedAsc: number[], p: number): number => {
  if (!sortedAsc.length) return 0;
  if (sortedAsc.length === 1) return sortedAsc[0] ?? 0;

  const idx = (sortedAsc.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo] ?? 0;

  const t = idx - lo;
  return (sortedAsc[lo] ?? 0) * (1 - t) + (sortedAsc[hi] ?? 0) * t;
};

const rmsOfAmps = (amps: number[]): number => {
  if (!amps.length) return 0;
  let sumSq = 0;
  for (const amp of amps) sumSq += amp * amp;
  return Math.sqrt(sumSq / amps.length);
};

const maxOfAmps = (amps: number[]): number => {
  if (!amps.length) return 0;
  let maxAmp = 0;
  for (const amp of amps) {
    if (amp > maxAmp) maxAmp = amp;
  }
  return maxAmp;
};

/** Baseline: RMS of per-pair peak amplitudes in the bucket. */
export const aggregateBucketByRms: BucketAggregator = (amps) =>
  rmsOfAmps(amps);

/** P75 of per-pair peak amplitudes in the bucket. */
export const aggregateBucketByP75: BucketAggregator = (amps) => {
  if (!amps.length) return 0;
  return percentileLinear([...amps].sort((a, b) => a - b), 0.75);
};

/** P90 of per-pair peak amplitudes in the bucket. */
export const aggregateBucketByP90: BucketAggregator = (amps) => {
  if (!amps.length) return 0;
  return percentileLinear([...amps].sort((a, b) => a - b), 0.9);
};

/** Altitude: max per-pair peak amplitude in the bucket. */
export const aggregateBucketByMax: BucketAggregator = (amps) =>
  maxOfAmps(amps);

/** Weighted mix of RMS and altitude (max). */
export const aggregateBucketByWeighted: BucketAggregator = (amps) => {
  if (!amps.length) return 0;
  return (
    WEIGHTED_RMS_RATIO * rmsOfAmps(amps) +
    (1 - WEIGHTED_RMS_RATIO) * maxOfAmps(amps)
  );
};

const BUCKET_AGGREGATORS: Record<WaveformBucketAggMode, BucketAggregator> = {
  rms: aggregateBucketByRms,
  p75: aggregateBucketByP75,
  p90: aggregateBucketByP90,
  max: aggregateBucketByMax,
  weighted: aggregateBucketByWeighted,
};

/**
 * Downsample audiowaveform min/max peaks to `targetPairs` by bucketing, then
 * aggregating with the given mode (defaults to ACTIVE_WAVEFORM_BUCKET_AGG).
 *
 * Not true PCM RMS — BE only stores min/max.
 */
export const downsamplePeaksToBucketPairs = (
  peaks: WaveformPeaksInput | undefined | null,
  targetPairs: number,
  mode: WaveformBucketAggMode = ACTIVE_WAVEFORM_BUCKET_AGG,
): number[] | undefined => {
  const flat = peaks ? toFlatPeaks(peaks) : null;
  if (!flat || flat.length < 2 || targetPairs <= 0) return undefined;

  const pairCount = Math.floor(flat.length / 2);
  if (pairCount <= 0) return undefined;

  // Fewer source pairs than bars: keep original (no upsample)
  if (pairCount <= targetPairs) {
    return flat.slice(0, pairCount * 2);
  }

  const aggregate = BUCKET_AGGREGATORS[mode];
  const result: number[] = new Array(targetPairs * 2);

  for (let i = 0; i < targetPairs; i++) {
    const startPair = Math.floor((i * pairCount) / targetPairs);
    const endPair = Math.floor(((i + 1) * pairCount) / targetPairs);
    const amps: number[] = [];

    for (let p = startPair; p < endPair; p++) {
      const min = flat[p * 2] ?? 0;
      const max = flat[p * 2 + 1] ?? 0;
      amps.push(peakAmplitude(min, max));
    }

    const amp = aggregate(amps);
    result[i * 2] = -amp;
    result[i * 2 + 1] = amp;
  }

  return result;
};